import { useCallback, useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  API_BASE_URL,
  listAgents,
  updateAgent,
  updateChatAgent,
  fetchLlmDetails,
  getAgentTransferNumbers,
  updateLlm,
  updateRetellAgent,
} from "../Store/apiStore";
import decodeToken from "../lib/decodeToken";
// [FIX: python-agent-edit] backend switch (rexpt.in / port 3001 = Python)
import { isPythonBackend, authHeaders } from "../utils/apiBackend";
import { getAgentPrompt, useAgentPrompt } from "./useAgentPrompt";
import getTimezoneFromState from "../lib/timeZone";
import {
  appointmentBooking,
  getBusinessSpecificFields,
} from "../lib/post_Call_analysis";
import { TruckElectric } from "lucide-react";
import { getAgentConfig } from "../lib/AgentConfig";
// import { createAgent, updateAgent } from '../api'; // adjust path

const getFromStorage = (key, fallback = "") =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || fallback;

const normalizeCondition = (s = "") =>
  String(s).trim().replace(/\s+/g, " ").replace(/_/g, " ");

const keyFromCondition = (s = "") =>
  `${normalizeCondition(s).toLowerCase().replace(/\s+/g, "_")}_number`;

const titleCase = (s = "") =>
  normalizeCondition(s)
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

function dedupeToolsByName(tools = []) {
  const seen = new Set();
  const out = [];

  for (const t of tools) {
    const name = String(t?.name || "").trim();
    if (!name) continue;

    if (seen.has(name)) continue;
    seen.add(name);
    out.push(t);
  }

  return out;
}

const dbRowToTransfer = (r) => {
  const condition = normalizeCondition(r?.condition || "");
  const dial = String(r?.dialCode || "").replace(/\D/g, "");
  const phoneRaw = String(r?.phone || "").replace(/\D/g, "");

  if (!condition || !dial || !phoneRaw) return null;
  const national = phoneRaw.startsWith(dial)
    ? phoneRaw.slice(dial.length)
    : phoneRaw;
  return {
    id: r?.id ?? null,
    condition,
    dialCode: dial,
    phone: dial + national,
    countryCode: String(r?.countryCode || "us").toLowerCase(),
  };
};

const prepareTransfersWithDialCode = (list = []) =>
  (list || [])
    .map((t) => ({
      ...t,
      condition: normalizeCondition(t.condition || ""),
      phone: String(t.phone || "").replace(/\D/g, ""),
      dialCode: String(t.dialCode || "").replace(/\D/g, ""),
      countryCode: String(t.countryCode || "us").toLowerCase(),
    }))
    .filter((t) => t.condition && t.phone && t.dialCode);

const buildTransferPromptAndTool = (formattedTransfers) => {
  const labels = formattedTransfers
    .map((t) => titleCase(t.condition))
    .join(", ");

  const lines = formattedTransfers.map((t) => {
    const label = titleCase(t.condition);
    const key = keyFromCondition(t.condition);
    return `If they say ${label}, transfer to {{${key}}}.`;
  });

  const fullPrompt = `The user might ask to be transferred to departments or specific people.

## AVAILABLE DEPARTMENTS FOR TRANSFER:
${formattedTransfers
      .map(
        (t) => `- ${titleCase(t.condition)}: {{${keyFromCondition(t.condition)}}}`
      )
      .join("\n")}

## CRITICAL RULES:
1. **ONLY transfer to departments listed above.** Do NOT transfer to any department not in this list.

2. **If caller needs a department NOT in the list** (like Service, Estimating, Construction, Accounts, etc.):
   - DO NOT attempt transfer
   - DO NOT use a different department's number as substitute
   - Say: "I apologize, I don't have a direct line for [requested department] at the moment. Currently, I can only connect you with: ${formattedTransfers
      .map((t) => titleCase(t.condition))
      .join(
        ", "
      )}. Would you like me to connect you with one of these, or would you prefer I take a message and have someone call you back?"

3. **For URGENT issues** (burst pipes, gas leaks, flooding) where Service is needed but unavailable:
   - Say: "I understand this is urgent. Unfortunately, I cannot transfer directly to our Service team right now. Let me take your details and mark this as URGENT so someone can call you back immediately. Can I get your name and phone number?"
   - DO NOT transfer to Billing or any other department for urgent service issues.

4. **Unknown person requests**: If caller asks for a person not listed, say: "I don't have a direct line for that person. Can I help you with something else, or would you like me to take a message?"
5. **If the transfer fails and the caller asks to try again, attempt the transfer once more.
## Transfer Mappings:
${lines.join("\n")}

**IMPORTANT: Use the appropriate number ONLY when there's an EXACT match with AVAILABLE DEPARTMENTS. Never substitute one department for another.**`;

  const timestamp = Date.now();

  return {
    type: "transfer_call",
    name: `transfer_on_inferred_${timestamp}`,
    transfer_destination: { type: "inferred", prompt: fullPrompt },
    transfer_option: {
      type: "cold_transfer",
      enable_human_detection: true,
      auto_greet: true,
      agent_detection_timeout_ms: 15000,
      enable_bridge_audio_cue: true,
    },
    custom_sip_headers: {
      "X-Telnyx-Username": `${process.env.REACT_APP_TELNYX_USERNAME}`
    },
    speak_during_execution: true,
    speak_after_execution: true,
    failure_message:
      "I apologize, but the line is busy right now. Please try again later or I can assist you with something else.",
  };
};

export const useAgentCreator = ({
  stepValidator = () => true,
  setLoading = () => { },
  setPopupMessage = () => { },
  setPopupType = () => { },
  setShowPopup = () => { },
  navigate = () => { },
  setHasFetched = () => { },
}) => {
  const token = localStorage.getItem("token") || "";
  const sessionBusinessiD = sessionStorage.getItem("bId");
  const state = sessionStorage.getItem("state");
  const decodeTokenData = decodeToken(token);
  const teamOwnerId = typeof window !== "undefined" ? localStorage.getItem("teamOwnerId") : null;
  const effectiveUserId = teamOwnerId || decodeTokenData?.ownerId || decodeTokenData?.id || "";
  const [userId, setUserId] = useState(effectiveUserId);
  const isUpdating = localStorage.getItem("UpdationMode") == "ON";
  // const sessionBusinessiD=JSON.parse(sessionStorage.getItem("businessId"))
  // const businessId = sessionBusinessiD|| sessionBusinessiD?.businessId ;
  const [agentCount, setAgentCount] = useState(0);
  const [transfers, setTransfers] = useState([]);

  const fetchAgentCountFromUser = async () => {
    try {
      const response = await listAgents();
      const filterAgents = await response.filter(
        (res) => res.userId === userId
      );
      setAgentCount(filterAgents.length);
    } catch (error) {
      console.log(error);
    }
  };

  const removeSpaces = (phone) => {
    if (!phone) return null;
    return phone.replace(/\s+/g, "");
  };

  useEffect(() => {
    fetchAgentCountFromUser();
  }, []);
  ///extractPromptVariables
  function extractPromptVariables(template, dataObject) {
    const matches = [...template.matchAll(/{{(.*?)}}/g)];
    const uniqueVars = new Set(matches.map((m) => m[1].trim()));

    // Flatten dataObject to a key-value map
    const flatData = {};

    function flatten(obj) {
      for (const key in obj) {
        const val = obj[key];
        if (
          typeof val === "object" &&
          val !== null &&
          "key" in val &&
          "value" in val
        ) {
          flatData[val.key.trim()] = val.value;
        } else if (typeof val === "object" && val !== null) {
          flatten(val); // Recursively flatten nested objects
        }
      }
    }

    flatten(dataObject);

    return Array.from(uniqueVars).map((variable) => ({
      name: variable,
      value: flatData[variable] ?? null,
      status: true,
    }));
  }
  const handleCreateAgent = useCallback(async () => {
    const isValid = stepValidator();
    if (!isValid) return;
    const packageMap = {
      Free: 1,
      Starter: 2,
      Scaler: 3,
      Growth: 4,
      Corporate: 5,
      Enterprise: 6,
    };

    const Buisness = JSON.parse(sessionStorage.getItem("businessDetails"));
    const businessType =
      Buisness?.businessType === "Other"
        ? Buisness?.customBuisness
        : Buisness?.businessType;
    const role_title =
      sessionStorage.getItem("agentRole") || "General Receptionist";
    const business =
      JSON.parse(sessionStorage.getItem("businessDetails")) ||
      "Your Business Name";
    const getBusinessNameFormCustom = sessionStorage.getItem(
      "displayBusinessName"
    );
    const getBusinessNameFromGoogleListing = JSON.parse(
      sessionStorage.getItem("placeDetailsExtract")
    );
    const agentNote = sessionStorage.getItem("agentNote") || "";
    const rawCustomServices =
      JSON.parse(sessionStorage.getItem("selectedCustomServices")) || [];
    const cleanedCustomServices = rawCustomServices
      .map((item) => item?.service?.trim())
      .filter(Boolean)
      .map((service) => ({ service }));
    const SelectedServices =
      JSON.parse(sessionStorage.getItem("businesServices")) ||
      "Your Business Name";
    const BusinessLocation =
      JSON.parse(sessionStorage.getItem("businessLocation")) ||
      "Your Business Services";
    const aboutBusinessForm =
      JSON.parse(sessionStorage.getItem("aboutBusinessForm")) ||
      "Your Business Services";
    const CallRecording = sessionStorage.getItem("callRecording") === "true";

    const agentGender = sessionStorage.getItem("agentGender");
    const languageSelect = sessionStorage?.getItem("agentLanguage");
    const plan = sessionStorage.getItem("plan");
    const languageAccToPlan = ["Scaler", "Growth", "Corporate"].includes(plan)
      ? "multi"
      : sessionStorage.getItem("agentLanguageCode");
    const agentName = sessionStorage.getItem("agentName") || "";
    const packageName = sessionStorage.getItem("package") || "Free";
    const chat_agent_id = sessionStorage.getItem("chat_agent_id");
    const chat_llm_id = sessionStorage.getItem("chat_llm_id");
    const sanitize = (str) =>
      String(str || "")
        .trim()
        .replace(/\s+/g, "_");
    const packageValue = packageMap[packageName] || 1;
    const businessServices = SelectedServices?.selectedService || [];
    const customServices =
      cleanedCustomServices?.map((item) =>
        typeof item === "string" ? item : item?.service
      ) || [];

    const businessServiceNames = businessServices?.map((item) => item);
    const allServices = [...customServices, ...businessServices];

    const commaSeparatedServices = allServices;
    const commaSeparatedServicesForServices = (
      allServices?.join(", ").replace("Other", "") || "Your Business Services"
    )
      .split(",")
      .filter((service) => service.trim() !== "")
      .map((service) => `- ${service.trim()}`)
      .join("\n");
    const dynamicAgentName = `${sanitize(businessType)}_${sanitize(
      getBusinessNameFromGoogleListing?.businessName ||
      getBusinessNameFormCustom
    )}_${sanitize(role_title)}_${packageValue}#${agentCount}`;

    const CustomservicesArray =
      cleanedCustomServices?.map((item) => item.service) || [];
    const businessPhone = removeSpaces(getBusinessNameFromGoogleListing?.phone);
    const timeZone = await getTimezoneFromState(
      getBusinessNameFromGoogleListing?.state
    );
    const servicesArray = commaSeparatedServicesForServices
      .split("\n")
      .map((line) => line.replace(/^-\s*/, "").trim())
      .filter((line) => line !== "")
      .map((service) => ({ service }));
    const rawPromptTemplate = getAgentPrompt({
      industryKey:
        business?.businessType == "Other"
          ? business?.customBuisness
          : business?.businessType,
      roleTitle: sessionStorage.getItem("agentRole"),
      agentName: "{{AGENT NAME}}",
      agentGender: "{{AGENT GENDER}}",
      business: {
        businessName: "{{BUSINESS NAME}}",
        email: "{{BUSINESS EMAIL ID}}",
        aboutBusiness: "{{MORE ABOUT YOUR BUSINESS}}",
        address: "{{BUSINESS ADDRESS}}",
      },
      languageSelect: "{{LANGUAGE}}",
      businessType: "{{BUSINESSTYPE}}",
      aboutBusinessForm: "{{}}",
      commaSeparatedServices: "{{SERVICES}}",
      agentNote: "{{AGENTNOTE}}",
      timeZone: "{{TIMEZONE}}",
    });

    let agentGeneralTools = [];
    try {
      agentGeneralTools = JSON.parse(
        sessionStorage.getItem("agentGeneralTools")
      );
    } catch (error) {
      agentGeneralTools = [];
      console.log("Json error while parsing agent tools");
    }

    let tools = agentGeneralTools;
    let filteredTransfers = [];
    if (typeof agentGeneralTools === "string") {
      try {
        agentGeneralTools = JSON.parse(tools);
      } catch (e) {
        console.error("Invalid JSON for agentGeneralTools", e);
        agentGeneralTools = [];
      }
    }

    if (Array.isArray(agentGeneralTools)) {
      // Set transfers only if tools exist
      if (Array.isArray(agentGeneralTools) && agentGeneralTools.length > 0) {
        // Optional: filter only those tools that have required fields
        filteredTransfers = agentGeneralTools
          .filter((tool) => tool.condition && tool.phone && tool.dialCode)
          .map((tool) => ({
            condition: tool.condition,
            phone: tool.phone,
            dialCode: tool.dialCode,
            countryCode: tool.countryCode || "",
          }));
      }
    }

    // Generate dynamic variables from formattedTransfers
    const salesEntry = filteredTransfers.find(
      (t) => t?.condition?.toLowerCase() === "sales"
    );
    const billingEntry = filteredTransfers.find(
      (t) => t?.condition?.toLowerCase() === "billing"
    );
    const supportEntry = filteredTransfers.find(
      (t) => t?.condition?.toLowerCase() === "support"
    );

    const dynamicVars = {
      sales_number: salesEntry
        ? `+${salesEntry.dialCode}${salesEntry.phone}`
        : "",
      billing_number: billingEntry
        ? `+${billingEntry.dialCode}${billingEntry.phone}`
        : "",
      support_number: supportEntry
        ? `+${supportEntry.dialCode}${supportEntry.phone}`
        : "",
    };

    // Remove any empty numbers
    Object.keys(dynamicVars).forEach((key) => {
      if (!dynamicVars[key]) delete dynamicVars[key];
    });

    // console.log('transfers',filteredTransfers,dynamicVars)
    const filledPrompt = getAgentPrompt({
      industryKey: business?.businessType,
      roleTitle: role_title,
      agentName: agentName,
      agentGender: agentGender,
      business: {
        businessName:
          getBusinessNameFromGoogleListing?.businessName ||
          getBusinessNameFormCustom,
        email: getBusinessNameFromGoogleListing?.email || "",
        aboutBusiness:
          getBusinessNameFromGoogleListing?.aboutBusiness ||
          getBusinessNameFromGoogleListing?.aboutBussiness,
        address: getBusinessNameFromGoogleListing?.address || "",
      },
      languageSelect: languageSelect,
      businessType,
      aboutBusinessForm,
      commaSeparatedServices,
      agentNote,
      timeZone: timeZone?.timezoneId,
      languageAccToPlan,
      plan,
      CallRecording,
    });

    const getLeadTypeChoices = () => {
      const fixedChoices = [
        "Spam Caller",
        "Irrelvant Call",
        "Angry Old Customer",
      ];
      const allServices = [...customServices, ...businessServiceNames];
      const cleanedServices = allServices
        .map((service) => service?.trim())
        .filter((service) => service && service?.toLowerCase() !== "other")
        .map((service) => {
          const normalized = service?.replace(/\s+/g, " ")?.trim();
          return `Customer for ${normalized}`;
        });
      const combinedChoices = Array.from(
        new Set([...fixedChoices, ...cleanedServices])
      );
      return combinedChoices;
    };
    //updation here
    if (isValid && localStorage.getItem("UpdationMode") == "ON") {
      setLoading(true);
      if (
        isValid == "EditBusinessType" ||
        isValid == "EditServicesOffered" ||
        isValid == "BusinessLocation"
      ) {
        const businessDetails = JSON.parse(
          sessionStorage.getItem("businessDetails")
        );
        const locationData = JSON.parse(
          sessionStorage.getItem("businessLocation")
        );
        const buisenessServices = JSON.parse(
          sessionStorage.getItem("businesServices")
        );
        const rawCustomServices =
          JSON.parse(sessionStorage.getItem("selectedCustomServices")) || [];
        const cleanedCustomServices = rawCustomServices
          .map((item) => item?.service?.trim())
          .filter(Boolean)
          .map((service) => ({ service }));
        try {
          const response = await axios.patch(
            `${API_BASE_URL}/businessDetails/updateBusinessDetailsByUserIDandBuisnessID/${userId}?businessId=${sessionBusinessiD}`,
            {
              businessName:
                getBusinessNameFromGoogleListing?.businessName ||
                getBusinessNameFormCustom,
              businessSize: businessDetails.businessSize,
              businessType: businessDetails.businessType,
              buisnessEmail: buisenessServices?.email,
              buisnessService: buisenessServices?.selectedService,
              customBuisness: businessDetails?.customBuisness || "",
              customServices: cleanedCustomServices,
              subType: businessDetails?.subType,
            }
          );

          if (sessionStorage.getItem("prevBuisnessType")) {
            sessionStorage.removeItem("prevBuisnessType");
          }
          // console.log('updation response', response)
        } catch (error) {
          console.log("error while buinsess details updated");
          // setLoading(false)
          return;
        }
      }

      const storedKnowledgeBaseId = sessionStorage.getItem("knowledgeBaseId");
      const llm_id =
        localStorage.getItem("llmId") || sessionStorage.getItem("llmId");

      let dbTransfers = [];
      let transferDynamicVarsFromDB = {};

      try {
        const agent_id =
          sessionStorage.getItem("agent_id") ||
          localStorage.getItem("agent_id");

        if (agent_id) {
          const dbRes = await getAgentTransferNumbers(agent_id);
          const rows =
            dbRes?.data?.data || dbRes?.data || dbRes?.rows || dbRes || [];

          if (Array.isArray(rows)) {
            const rawDbTransfers = rows.map(dbRowToTransfer).filter(Boolean);

            const formattedTransfers =
              prepareTransfersWithDialCode(rawDbTransfers);

            dbTransfers = formattedTransfers;
            formattedTransfers.forEach((t) => {
              const key = keyFromCondition(t.condition);
              transferDynamicVarsFromDB[key] = `+${t.phone}`;
            });
          }
        }
      } catch (e) {
        console.error("Edit flow: failed to load DB transfers:", e);
      }

      const agentConfig = {
        version: 0,
        model: "gemini-2.0-flash",
        model_temperature: 0,
        model_high_priority: true,
        tool_call_strict_mode: true,
        general_prompt: filledPrompt,
        starting_state: "information_collection",
        default_dynamic_variables: {
          customer_name: "John Doe",
          timeZone: timeZone?.timezoneId,
          business_Phone: businessPhone,
          business_email: business.email,
          customer_phone: "{{customer_phone}}",
          email: "",
          ...dynamicVars,
        },
        general_tools: [
          {
            type: "end_call",
            name: "end_call",
            description: "End the call with user.",
          },
          {
            type: "extract_dynamic_variable",
            name: "extract_user_details",
            description:
              "Extract the user's details like name, email, phone number, address, and reason for calling from the conversation",
            variables: [
              {
                type: "string",
                name: "email",
                description:
                  "Extract the user's email address from the conversation",
              },
              {
                type: "number",
                name: "phone",
                description:
                  "Extract the user's phone number from the conversation",
              },
              {
                type: "string",
                name: "address",
                description: "Extract the user's address from the conversation",
              },
              {
                type: "string",
                name: "reason",
                description:
                  "Extract the user's reason for calling from the conversation",
              },
              {
                type: "string",
                name: "name",
                description: "Extract the user's name from the conversation\"",
              },
              {
                type: "boolean",
                name: "known_caller",
                description:
                  "True if the caller is recognized as an existing customer/previous caller, otherwise false.",
              },
              {
                type: "string",
                name: "caller_name",
                description:
                  "If known_caller is true, extract the caller's name (or best available identifier). Otherwise empty string.",
              },
            ],
          },
          {
            name: "get_conversation_history",
            description:
              "Fetch previous coversation history by customer email address",
            type: "custom",
            method: "POST",
            url: `${API_BASE_URL}/Chatbot/get_chat_logs?email={{parameter.email}}`,
            // url: `https://94eee0a16be3.ngrok-free.app/Chatbot/get_chat_logs?email={{parameter.email}}`,
            speak_after_execution: true,

            // Query parameters for GET request
            query_params: {
              email: "{{parameter.email}}",
            },

            // Response variables to extract order status
            parameters: {
              type: "object",
              properties: {
                email: {
                  type: "string",
                  description:
                    "Customer email address to fetch conversation history",
                },
              },
              required: ["email"],
            },
          },
          {
            name: "book_calendar_event",
            description:
              "Create a Google Calendar meeting with details such as title, description, timing, attendees, and reminders.",
            type: "custom",
            method: "POST",
            // url: `https://94eee0a16be3.ngrok-free.app/api/create-meeting`,
            // url: `https://9280a12eb35e.-free.app/api/create-meeting`,
            url: `${API_BASE_URL}/create-meeting`,
            speak_after_execution: true,
            // Body parameters (payload sent in the request)
            body: {
              email: "{{parameter.userId}}",
              name: "{{parameter.name}}",

              // title: "{{parameter.title}}",
              // description: "{{parameter.description}}",
              // start: "{{parameter.start}}",
              // end: "{{parameter.end}}",
              // timezone: "{{parameter.timezone}}",
              // attendees: "{{parameter.attendees}}",
            },

            // Parameter schema for chatbot input validation
            parameters: {
              type: "object",
              properties: {
                title: {
                  type: "string",
                  description: "Title of the calendar event",
                },
                description: {
                  type: "string",
                  description: "Details or agenda of the event",
                },
                start: {
                  type: "string",
                  description:
                    "Event start datetime in ISO format (e.g. 2025-10-27T15:00:00+05:30)",
                },
                end: {
                  type: "string",
                  description:
                    "Event end datetime in ISO format (e.g. 2025-10-27T16:00:00+05:30)",
                },
                timezone: {
                  type: "string",
                  description: "Timezone for the event (default: UTC)",
                  default: "UTC",
                },
                attendees: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      email: { type: "string" },
                      displayName: { type: "string" },
                    },
                  },
                  description:
                    "List of attendee email addresses and display names",
                },
                location: {
                  type: "string",
                  description: "Location or meeting link of the event",
                },
                conference: {
                  type: "boolean",
                  description:
                    "Whether to create a Google Meet link automatically (default: true)",
                  default: true,
                },
                reminders: {
                  type: "object",
                  properties: {
                    email: {
                      type: "number",
                      description:
                        "Minutes before event to send email reminder",
                    },
                    popup: {
                      type: "number",
                      description:
                        "Minutes before event to show popup reminder",
                    },
                  },
                },
                sendUpdates: {
                  type: "string",
                  enum: ["all", "externalOnly", "none"],
                  description:
                    "Who should receive updates when event changes (default: all)",
                  default: "all",
                },
                idempotencyKey: {
                  type: "string",
                  description: "Unique key to prevent duplicate event creation",
                },
                calendarId: {
                  type: "string",
                  description: "Google Calendar ID (default: primary)",
                  default: "primary",
                },
              },
              required: ["title", "start", "end", "name", "email"],
            },
          },

          ...getAgentConfig(businessType),
        ],
      };
      if (isValid == "BusinessListing" || isValid == "EditBusinessDetail") {
        agentConfig.knowledge_base_ids = [storedKnowledgeBaseId];
      }
      //Create LLm
      try {
        // [FIX: python-agent-edit] Python agents have no Retell LLM/agent to
        // update. The business rows are already saved (updateBusinessDetails /
        // updateKnowledeBase above), so for a Python backend we skip ALL the
        // Retell calls below and hit one Python endpoint that regenerates the
        // prompt from the new business data and rebuilds the knowledge base.
        // Node backend: isPythonBackend() is false → this block is skipped and
        // the existing Retell flow runs completely unchanged.
        if (isPythonBackend()) {
          const agentIdForEdit =
            sessionStorage.getItem("agent_id") ||
            localStorage.getItem("agent_id") ||
            localStorage.getItem("agentId");
          try {
            // Send the freshly-rendered prompt (rawPromptTemplate was built
            // above from the just-updated sessionStorage business data via
            // getAgentPrompt), so edit produces the SAME prompt as create.
            // If this is ever missing, the Python /edit falls back to
            // build_prompt() from the updated business row.
            //
            // Also send the selected website pages the SAME way Retell's Save
            // does (from sessionStorage selectedSiteMapUrls / scrapedUrls). This
            // makes Python's KB rebuild match Retell's URL priority: if the user
            // changed the website URL, the frontend already re-crawled it and
            // these hold the fresh pages → the rebuild uses them. On a plain
            // business edit these are just the unchanged stored pages, so the
            // rebuild reuses them (no forced re-crawl → KB can't shrink).
            let selectedSitemapUrls = [];
            try {
              const rawSel =
                JSON.parse(
                  sessionStorage.getItem("selectedSiteMapUrls") || "[]"
                ) ||
                JSON.parse(sessionStorage.getItem("scrapedUrls") || "[]") ||
                [];
              // Stored as [{url, checkedStatus}] or plain strings. Send only the
              // CHECKED pages (the set the user chose to index), as plain URL
              // strings — the same pages Retell rebuilds from. If nothing is
              // checked/parsable, send [] so the backend falls back to the stored
              // scrapedUrls (no forced re-crawl).
              selectedSitemapUrls = (Array.isArray(rawSel) ? rawSel : [])
                .map((it) =>
                  typeof it === "string"
                    ? it
                    : it && it.checkedStatus
                    ? it.url
                    : null
                )
                .filter(Boolean);
            } catch (_) {
              selectedSitemapUrls = [];
            }
            await fetch(
              `${process.env.REACT_APP_PY_API_BASE_URL}/agents/${agentIdForEdit}/edit`,
              {
                method: "POST",
                headers: authHeaders(),
                // Python speaks the stored prompt directly. Unlike Retell, it
                // has no separate prompt-variable map, so send the rendered
                // prompt (with the real agent/business values), not the raw
                // {{PLACEHOLDER}} template.
                body: JSON.stringify({
                  rawPromptTemplate: filledPrompt,
                  selectedSitemapUrls,
                }),
              }
            );
            setPopupType("success");
            const label =
              {
                EditBusinessType: "Business Type",
                EditServicesOffered: "Services Offered",
                EditNameAvtar: "Name & Avtar",
                EditLanguage: "Agent Language",
                EditGender: "Agent Gender / Voice ",
                EditBusinessDetail: "Business Detail",
                BusinessListing: "Business Detail",
              }[isValid] || "Details";
            setPopupMessage(`${label} Updated Successfully`);
            setShowPopup(true);
            const nextRoute = sessionStorage.getItem("nextEditRoute");
            if (sessionStorage.getItem("naviateFrom") !== "callRecording") {
              setTimeout(() => {
                if (nextRoute) {
                  sessionStorage.removeItem("nextEditRoute");
                  navigate(nextRoute, { replace: true });
                } else {
                  navigate("/edit-agent", { replace: true });
                }
              }, 1500);
            }
            setHasFetched(false);
          } catch (e) {
            console.error("Python agent edit failed:", e);
            setPopupType("failed");
            setPopupMessage("Update failed. Please try again.");
            setShowPopup(true);
          } finally {
            setLoading(false);
          }
          return; // Python path done — do not fall through to the Retell flow.
        }

        const existingLlm = await fetchLlmDetails(llm_id);
        const currentConfig =
          existingLlm?.data?.data || existingLlm?.data?.data?.data || {};

        const existingVars = currentConfig?.default_dynamic_variables || {};
        const existingTools = currentConfig?.general_tools || [];

        let finalTransferTool = null;

        if (dbTransfers.length > 0) {
          finalTransferTool = buildTransferPromptAndTool(dbTransfers);
        }

        const toolsWithoutOldTransfer = (
          agentConfig.general_tools || []
        ).filter(
          (t) =>
            !(
              t?.type === "transfer_call" &&
              String(t?.name || "").startsWith("transfer_on_inferred_")
            )
        );

        const existingWithoutOldTransfer = (existingTools || []).filter(
          (t) =>
            !(
              t?.type === "transfer_call" &&
              String(t?.name || "").startsWith("transfer_on_inferred_")
            )
        );

        const mergedTools = [
          ...existingWithoutOldTransfer,
          ...toolsWithoutOldTransfer,
          ...(finalTransferTool ? [finalTransferTool] : []),
        ];

        agentConfig.default_dynamic_variables = {
          ...existingVars,
          ...agentConfig.default_dynamic_variables,
          ...transferDynamicVarsFromDB,
        };

        agentConfig.general_tools = dedupeToolsByName(mergedTools);

        const llmRes = await updateLlm(token, llm_id, agentConfig);

        if (!llmRes?.success) {
          throw new Error(llmRes?.message || "LLM update failed");
        }

        sessionStorage.setItem("llmId", llmRes?.data?.llm_id || llm_id);
        //agent updation
        if (
          isValid == "EditBusinessType" ||
          isValid == "EditServicesOffered" ||
          isValid == "EditLanguage" ||
          isValid == "EditGender" ||
          isValid == "EditNameAvtar" ||
          isValid == "BusinessListing" ||
          isValid == "EditBusinessDetail"
        ) {
          const finalAgentData = {
            voice_id: sessionStorage.getItem("agentVoice") || "11labs-Adrian",
            language: sessionStorage.getItem("agentLanguageCode") || "en-US",
            agent_name: dynamicAgentName || sessionStorage.getItem("agentName"),
            language: languageAccToPlan,
            post_call_analysis_model: "gpt-4o-mini",
            responsiveness: 1,
            enable_backchannel: true,
            interruption_sensitivity: 0.91,
            backchannel_frequency: 0.7,
            ring_duration_ms: 15000,
            backchannel_words: [
              "Got it",
              "Yeah",
              "Uh-huh",
              "Understand",
              "Ok",
              "hmmm",
            ],
            post_call_analysis_data: [
              {
                type: "enum",
                name: "lead_type",
                description: "Feedback given by the customer about the call.",
                choices: getLeadTypeChoices(),
              },
              {
                type: "string",
                name: "name",
                description: "Extract the user's name from the conversation",
                examples: [
                  "Ajay Sood",
                  "John Wick",
                  "Adam Zampa",
                  "Jane Doe",
                  "Nitish Kumar",
                  "Ravi Shukla",
                ],
              },
              {
                type: "string",
                name: "email",
                description: "Extract the user's email from the conversation",
                examples: [
                  "john.doe@example.com",
                  "nitish@company.in",
                  "12@gmail.com",
                ],
              },
              {
                type: "string",
                name: "reason",
                description:
                  "The reason the user is calling or their inquiry. If provided in Hindi, translate to English. Summarize if it's long.",
                examples: [
                  "Schedule an appointment",
                  "Ask about services",
                  "Request for accounting help",
                ],
              },
              {
                type: "string",
                name: "address",
                description:
                  "Extract the user's personal address from the conversation. If the user explicitly provides their own address, include and format it for CRM or contact forms. If no personal address is mentioned, do not add or infer any business location. If the address is spoken in Hindi, translate it to English before returning.",
                examples: [
                  "123 Main St, Delhi",
                  "42 Wallaby Way, Sydney",
                  "1490 Aandhar Eleven",
                ],
              },
              {
                type: "number",
                name: "phone_number",
                description:
                  "The user's phone number in numeric format. If digits are spoken in words (e.g., 'seven eight seven six one two'), convert them to digits (e.g., '787612'). Ensure it's a valid number when possible.",
              },
              ...appointmentBooking(businessType),
              ...getBusinessSpecificFields(businessType),
            ],
            webhook_url: `${API_BASE_URL}/agent/updateAgentCall_And_Mins_WebHook`,
            // webhook_url: `https://a0c3-223-178-211-123.ngrok-free.app/api/agent/updateAgentCall_And_Mins_WebHook`,
            // webhook_url: `${API_BASE_URL}/agent/updateAgentCall_And_Mins_WebHook`,
            normalize_for_speech: true,
          };
          const agent_id =
            sessionStorage.getItem("agent_id") ||
            localStorage.getItem("agentId");
          try {
            const retellAgentRes = await updateRetellAgent(
              token,
              agent_id,
              finalAgentData
            );
            if (!retellAgentRes?.success) {
              throw new Error(
                retellAgentRes?.message || "Retell agent update failed"
              );
            }
            const agentId = retellAgentRes?.data?.agent_id;
            // Get businessId from sessionStorage
            const businessIdString = sessionStorage.getItem("businessId");
            // Convert string to object
            const businessIdObj = JSON.parse(businessIdString);
            const promptVariablesList = extractPromptVariables(
              rawPromptTemplate,
              {
                industryKey:
                  business?.businessType == "Other"
                    ? business?.customBuisness
                    : business?.businessType,
                roleTitle: sessionStorage.getItem("agentRole"),
                agentName: {
                  key: "AGENT NAME",
                  value: agentName?.split(" ")[0],
                },
                agentGender: { key: "AGENT GENDER", value: agentGender },
                business: {
                  businessName: {
                    key: "BUSINESS NAME",
                    value:
                      getBusinessNameFromGoogleListing?.businessName ||
                      getBusinessNameFormCustom,
                  },
                  email: {
                    key: "BUSINESS EMAIL ID",
                    value: getBusinessNameFromGoogleListing?.email || "",
                  },
                  aboutBusiness: {
                    key: "MORE ABOUT YOUR BUSINESS",
                    value:
                      getBusinessNameFromGoogleListing?.aboutBusiness ||
                      getBusinessNameFromGoogleListing?.aboutBussiness,
                  },
                  address: {
                    key: "BUSINESS ADDRESS",
                    value: getBusinessNameFromGoogleListing?.address || "",
                  },
                },
                languageSelect: {
                  key: "LANGUAGE",
                  value: languageSelect || "",
                },
                businessType: {
                  key: "BUSINESSTYPE",
                  value: businessType || "",
                },
                commaSeparatedServices: {
                  key: "SERVICES",
                  value: servicesArray || "",
                },
                timeZone: {
                  key: "TIMEZONE",
                  value: timeZone?.timezoneId || "",
                },
              }
            );
            // Now access the actual ID
            const agentData = {
              userId: userId,
              agent_id: agentId || sessionStorage.getItem("agentId"),
              knowledgeBaseId: sessionStorage.getItem("knowledgeBaseId"),
              llmId: sessionStorage.getItem("llmId"),
              avatar: sessionStorage.getItem("avatar") || "",
              agentVoice:
                sessionStorage.getItem("agentVoice") || "11labs-Adrian",
              agentAccent:
                sessionStorage.getItem("agentVoiceAccent") || "American",
              agentRole:
                sessionStorage.getItem("agentRole") || "Genral Receptionist",
              agentName: sessionStorage.getItem("agentName") || "",
              agentLanguageCode:
                sessionStorage.getItem("agentLanguageCode") || "en-US",
              agentLanguage:
                sessionStorage.getItem("agentLanguage") || "English (US)",
              agentGender: sessionStorage.getItem("agentGender") || "female",
              agentStatus: true,
              dynamicPromptTemplate: filledPrompt,
              rawPromptTemplate: rawPromptTemplate,
              promptVariablesList: JSON.stringify(promptVariablesList),
              timezone: timeZone?.timezoneId || "",
            };
            //update agent in DB
            try {
              const response = await updateAgent(agentId, agentData);
              //updateChatAgent
              const commonAgentPayload = {
                industryKey: business?.businessType,
                roleTitle: role_title,
                agentName: agentName,
                agentGender: agentGender,
                business: {
                  businessName:
                    getBusinessNameFromGoogleListing?.businessName ||
                    getBusinessNameFormCustom,
                  email: getBusinessNameFromGoogleListing?.email || "",
                  aboutBusiness:
                    getBusinessNameFromGoogleListing?.aboutBusiness ||
                    getBusinessNameFromGoogleListing?.aboutBussiness,
                  address: getBusinessNameFromGoogleListing?.address || "",
                },
                languageSelect: languageSelect,
                businessType,
                aboutBusinessForm,
                allServices,
                agentNote,
                timeZone: timeZone?.timezoneId,
                languageAccToPlan,
                plan,
                CallRecording,
                businessPhone,
                businessEmail: business?.email,
                chat_agent_id,
                chat_llm_id,
                agent_id: agentId,
              };
              // Check both IDs exist and are not null/empty/"null"
              if (
                chat_agent_id &&
                chat_llm_id &&
                chat_agent_id !== "null" &&
                chat_llm_id !== "null"
              ) {
                try {
                  const updateChatAgentResponse = await updateChatAgent(
                    commonAgentPayload,
                    token
                  );
                } catch (error) {
                  console.error("Error updating chat agent:", error);
                }
              } else {
                console.log(
                  "Chat agent or LLM ID not found — skipping API call."
                );
              }
              if (response.status === 200 || response.status === 201) {
                setPopupType("success");
                setPopupMessage("Agent Updated successfully!");
                setShowPopup(true);
              }
            } catch (error) {
              // console.log(error,error.status)
              if (error?.status == 400) {
                // console.log('errorinside',error)
                setPopupType("failed");
                setPopupMessage(error?.response?.data?.message);
                setShowPopup(true);
                setLoading(false);
              } else {
                console.error("Agent Updation failed:", error);
                setPopupType("failed");
                setPopupMessage(
                  "Agent Updation failed while saving data in Database. Please try again."
                );
                setShowPopup(true);
                setLoading(false);
              }
            }
          } catch (err) {
            console.error("Agent Updation failed:", err);
            setPopupType("failed");
            setPopupMessage("Agent Updation failed.");
            setShowPopup(true);
            setLoading(false);
          }
        }

        setPopupType("success");
        const screenLabels = {
          EditBusinessType: "Business Type",
          EditServicesOffered: "Services Offered",
          EditNameAvtar: "Name & Avtar",
          EditLanguage: "Agent Language",
          EditGender: "Agent Gender / Voice ",
          EditBusinessDetail: "Business Detail",
        };
        // console.log('isValidjsdjajdja',isValid)
        setPopupMessage(`${screenLabels[isValid]} Updated Succesfully`);
        setShowPopup(true);

        if (sessionStorage.getItem("naviateFrom") == "callRecording") {
          // keep as is
        } else {
          const nextRoute = sessionStorage.getItem("nextEditRoute"); 

          setTimeout(() => {
            if (nextRoute) {
              sessionStorage.removeItem("nextEditRoute"); 
              navigate(nextRoute, { replace: true });      
            } else {
              navigate("/edit-agent", { replace: true });  
            }
          }, 1500);
        }


        setHasFetched(false);
      } catch (error) {
        console.error("LLM updation failed:", error);
        setPopupType("failed");
        setPopupMessage("LLM updation failed. Please try again.");
        setShowPopup(true);
      } finally {
        setLoading(false);
      }

      setLoading(false);
    }
  }, [
    stepValidator,
    setLoading,
    setPopupMessage,
    setPopupType,
    setShowPopup,
    navigate,
    setHasFetched,
  ]);

  return { handleCreateAgent };
};
const cleanServiceArray = () => {
  try {
    let raw;
    if (localStorage.getItem("UpdationMode") != "ON") {
      raw = sessionStorage.getItem("businessDetails");
    } else {
      raw = raw = sessionStorage.getItem("businessDetails");
    }
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.selectedService)) {
      return parsed.selectedService;
    } else if (
      typeof parsed?.selectedService === "object" &&
      Array.isArray(parsed.selectedService.selectedService)
    ) {
      return parsed.selectedService.selectedService;
    }
    return [];
  } catch {
    return [];
  }
};
